import React, { useEffect, useState } from 'react';
import { withNavBar } from '../../shared/NavBar/NavBar';
import AgGridWrapper from '../../../shared/Grid/AgGridWrapper';
import { GridApi } from 'ag-grid-community';
import { TrashCan, Pencil, Download } from '../../../constants/Icons';
import Toast from '../../../shared/Notifications/Toast';
import ManageUsersApiHelpers from './ManageUsersApiHelpers';
import ManagerUserMapper from './ManageUsersMapper';
import ManageHabitatsApiHelpers from '../manageHabitats/ManageHabitatsApiHelpers';
import { ILocationServiceObj, IUserListServiceObj, IUserViewObj } from '../../../shared/apiServices/IApiInterfaces';
import './ManageUsers.scss';
import { Modal } from '../../../shared/Modal/Modal';
import EditUsers from '../../views/manageUsers/editUsers/EditUsers';
import { Preloader } from '../../../shared/Preloader/Preloader';

const userTypes = {
  admin: 'admin',
  nonadmin: 'nonadmin',
  all: 'all',
};

export const FormsIds = {
  location: 'location',
  lastName: 'lastname',
  adminCheckbox: 'adminChecbox',
  nonadminCheckbox: 'nonadminChecbox',
  allChecbox: 'allCheckbox',
  clearAllBtn: 'clearAllBtn',
  deleteUserBtnGrid: 'deleteUserBtnGrid',
  confirmUserDeletion: 'confirmUserDeletionBtn',
  cancelUserDeletion: 'cancelUserDeletionBtn',
}

const ManageUsers = () => {
  const [location, setLocation] = useState<string>();
  const [lastName, setLastName] = useState<string>();
  const [userType, setUserType] = useState<string>(userTypes.all);
  const [gridApi, setGridApi] = useState<GridApi>();
  const [locations, setLocations] = useState([] as ILocationServiceObj[]);
  const [userData, setUserData] = useState<IUserViewObj[]>([]);

  const [editModal, setEditModal] = useState<IModal>();
  const [deleteModal, setDeleteModal] = useState<IModal>();
  const [userToDelete, setUserToDelete] = useState<IUserViewObj | undefined>();
  const [userToEdit, setUserToEdit] = useState<IUserViewObj | undefined>();

  const [areLocationsLoading, setAreLocationsLoading] = useState(true);
  const [areUsersLoading, setAreUsersLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setInitialState();
  }, []);

  useEffect(() => {
    if (areUsersLoading || areLocationsLoading) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
  }, [areUsersLoading, areLocationsLoading])

  const setInitialState = async (byPassLocations: boolean = false) => {
    const newUsers = await getUsers();
    setUserData(newUsers);

    if (!byPassLocations) {
      const newLocations = await getLocations();
      setLocations(newLocations);
    }
  }

  const getLocations = async (): Promise<ILocationServiceObj[]> => {
    try {
      setAreLocationsLoading(true);
      const initialLocations: ILocationServiceObj[] =
        await ManageHabitatsApiHelpers.getAllHabitatLocations();
      return initialLocations;
    } catch (ex) {
      Toast.show('Error Getting Sites');
      console.error();
      return [];
    } finally {
      setAreLocationsLoading(false);
    }
  };

  const getUsers = async (): Promise<IUserViewObj[]> => {
    try {
      setAreUsersLoading(true);
      const userObj: IUserListServiceObj = await ManageUsersApiHelpers.getAllUsers();
      const adminGroupSet = new Set<string>(userObj.adminGroup.map(i => i.Username));

      const viewObj = userObj.users.map(user => {
        return ManagerUserMapper.getAttributes(user, adminGroupSet);
      });
      return viewObj;
    } catch (ex) {
      Toast.show('Error Getting Users');
      console.error(ex);
      return [];
    } finally {
      setAreUsersLoading(false);
    }
  };

  useEffect(() => {
    if (gridApi === undefined) return;

    if (location === '' && lastName === '' && userType === userTypes.all) {
      gridApi?.setFilterModel(null);
      return;
    }

    let filterModel: { [k: string]: any } = {};

    if (location && location?.length > 0) {
      filterModel.site = {
        filter: location,
        filterType: 'text',
        type: 'contains',
      };
    }

    if (lastName && lastName?.length > 0) {
      filterModel.lastName = {
        filter: lastName,
        filterType: 'text',
        type: 'contains',
      };
    }

    if (userType !== userTypes.all) {
      filterModel.admin = {
        filter: `${userType === userTypes.admin}`,
        filterType: 'text',
        type: 'contains',
      }
    }
    gridApi?.setFilterModel(filterModel);
  }, [location, lastName, userType]);

  const initialValues = () => {
    setLocation('');
    setLastName('');
    setUserType(userTypes.all);
  };

  const actionsRender = (params: any) => {
    //TODO: integrate actions for edit
    const { userId } = params.data as IUserViewObj;
    return (
      <span>
        <span className='action-icon' onClick={() => handleOpenEditUser(params?.data)}>{Pencil}</span>
        <span data-testid={`deleteUserBtnGrid-${userId}`} className='action-icon' onClick={() => handleOpenDeleteUser(params?.data)}>{TrashCan}</span>
      </span>
    );
  };

  const adminRender = (params: any) => {
    if (!params.value) return <></>;

    return (
      <div className='center-align'>
        <label>
          <input type='checkbox' checked={true} readOnly />
          <span></span>
        </label>
      </div>
    );
  };

  const columns = [
    { headerName: 'Location', field: 'site' },
    { headerName: 'First Name', field: 'firstName' },
    { headerName: 'Last Name', field: 'lastName' },
    { headerName: 'Username', field: 'username' },
    { headerName: 'Email Address', field: 'email' },
    {
      headerName: 'Admin User',
      field: 'admin',
      width: 140,
      cellRendererFramework: adminRender,
    },
    {
      headerName: 'Actions',
      sortable: false,
      filter: false,
      cellRendererFramework: actionsRender,
    },
  ];

  const onGridReady = (params: any) => {
    setGridApi(params.api);
  };

  const onDownloadClick = () => {
    if (gridApi) gridApi.exportDataAsCsv({ fileName: 'users.csv' });
  };

  const handleCloseDeleteUser = () => {
    setUserToDelete(undefined);
    deleteModal?.close();
  }

  const saveEditUser = (user: IUserViewObj) => {

    if (user) {
      const { userId, admin, username } = user;

      const editUser = async () => {
        try {
          await ManageUsersApiHelpers.updateUser(userId, admin);
          Toast.show(`${username} updated`);
        } catch (ex) {
          Toast.show('Error editing user');
          console.error(ex);
        } finally {
          await setInitialState(true);
        }

      }
      editUser();
      editModal?.close();
    }
  }


  const handleDeleteUser = () => {
    deleteModal?.close();
    if (userToDelete) {
      const { userId, username } = userToDelete;
      const deleteUser = async () => {
        try {
          await ManageUsersApiHelpers.deleteUser(userId);
          Toast.show(`${username} deleted`);
        } catch (ex) {
          Toast.show('Error deleting user');
          console.error(ex);
        } finally {
          await setInitialState(true);
        }

      }
      deleteUser();
    }
  }

  const handleOpenDeleteUser = (user: IUserViewObj) => {
    setUserToDelete(user);
    deleteModal?.open();
  }

  const searchTable = (
    <div className='search-table'>
      <div className='search-field'>
        <label className='active'>Location</label>
        <select
          data-testid={FormsIds.location}
          className='browser-default'
          value={location}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
            setLocation(e.target?.value || '')
          }
        >
          <option value=''>All</option>
          {locations.map((loc) => (
            <option key={loc.id}>{loc.name}</option>
          ))}
        </select>
      </div>
      <div className='search-field'>
        <label className='active'>Last Name</label>
        <input
          type='text'
          data-testid={FormsIds.lastName}
          value={lastName || ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setLastName(e.target?.value || '')
          }
        ></input>
      </div>
      <div className='search-field'>
        <label className='active'>User Type</label>
        <div className='user-type'>
          <label className='checkbox'>
            <input
              type='checkbox'
              id={userTypes.admin}
              data-testid={FormsIds.adminCheckbox}
              className='filled-in'
              checked={userTypes.admin === userType}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setUserType(e.target.id)
              }
            />
            <span>Admin</span>
          </label>
          <label className='checkbox'>
            <input
              type='checkbox'
              id={userTypes.nonadmin}
              data-testid={FormsIds.nonadminCheckbox}
              className='filled-in'
              checked={userTypes.nonadmin === userType}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setUserType(e.target.id)
              }
            />
            <span>Non-Admin</span>
          </label>
          <label className='checkbox'>
            <input
              type='checkbox'
              id={userTypes.all}
              data-testid={FormsIds.allChecbox}
              className='filled-in'
              checked={userTypes.all === userType}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setUserType(e.target.id)
              }
            />
            <span>All</span>
          </label>
        </div>
      </div>
      <div className='actions'>
        <button className='btn btn-tertiary right' data-testid={FormsIds.clearAllBtn} onClick={initialValues}>
          Clear All
        </button>
      </div>
    </div>
  );

  const toolbar = (
    <div className="users-grid-toolbar">
      <div className="users-grid-toolbar-chips" />
      <div className="users-grid-toolbar-actions">
        <span className="action-icon" onClick={onDownloadClick}>
          {Download}
        </span>
      </div>
    </div>
  );

  const userDeletionConfirmation = <Modal onLoad={setDeleteModal} title={`Are you sure you want to delete ${userToDelete?.username}?`} confimationModal={true}>
      <div className='row d-flex justify-content-end'>
        <div className='col offset-s4 d-flex d-center'>
          <button data-testid={FormsIds.cancelUserDeletion} className='btn-small button-secondary' onClick={handleCloseDeleteUser}>
            No
          </button>
        </div>
        <div className='col d-flex d-center'>
          <button data-testid={FormsIds.confirmUserDeletion} className='button-primary btn-small' onClick={handleDeleteUser}>
            Yes
          </button>
        </div>
		  </div>
  </Modal>

  const userEditConfiguration =
    <Modal onLoad={setEditModal} title={'Edit User'}>
      <EditUsers user={userToEdit} saveUser={saveEditUser} />
    </Modal>


  const handleOpenEditUser = (user: IUserViewObj) => {
    setUserToEdit(user);
    editModal?.open();
  }

  return (
    <div className="manage-users">
      <h3>Manage Users</h3>
      <p>Add, edit or remove users</p>
      {isLoading ? (
        <Preloader height="20rem" />
      ) : (
        <>
          {searchTable}
          {toolbar}
          <AgGridWrapper
            gridName={'ManageUser'}
            rowData={userData}
            columnDefs={columns}
            gridConfig={{
              onGridReady: onGridReady,
            }}
          />
        </>
      )}
      {userDeletionConfirmation}
      {userEditConfiguration}
    </div>
  );
};

export default withNavBar(ManageUsers, true);
